import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Heart, Copy, Check, Search, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const duaCategories = [
  { id: 'all', name: 'Të gjitha', icon: '📖' },
  { id: 'morning', name: 'Mëngjes', icon: '🌅' },
  { id: 'evening', name: 'Mbrëmje', icon: '🌙' },
  { id: 'prayer', name: 'Namaz', icon: '🕌' },
  { id: 'food', name: 'Ushqim', icon: '🍽️' },
  { id: 'travel', name: 'Udhëtim', icon: '✈️' },
  { id: 'protection', name: 'Mbrojtje', icon: '🛡️' },
  { id: 'forgiveness', name: 'Falje', icon: '🤲' },
  { id: 'daily', name: 'Ditore', icon: '📿' },
];

const defaultDuas = [
  {
    id: 1,
    category: 'morning',
    title: 'Duaja e Zgjimit nga Gjumi',
    albanian: 'Falënderimi i takon Allahut, i Cili na ringjalli pasi na bëri të vdekur dhe tek Ai është kthimi.',
    source: 'Buhariu',
    benefits: 'Falënderim për një ditë të re jete'
  },
  {
    id: 2,
    category: 'morning',
    title: 'Duaja e Mëngjesit',
    albanian: 'Arritëm në mëngjes dhe sundimi i takon Allahut, dhe falënderimi i takon Allahut.',
    source: 'Muslimi',
    benefits: 'Mbrojtje gjatë ditës'
  },
  {
    id: 3,
    category: 'morning',
    title: 'Kërkimi i Mbrojtjes në Mëngjes',
    albanian: 'O Allah, me Ty arritëm në mëngjes dhe me Ty arritëm në mbrëmje, me Ty jetojmë dhe me Ty vdesim, dhe tek Ti është ringjallja.',
    source: 'Tirmidhiu',
    benefits: 'Mbrojtje e plotë ditore'
  },
  {
    id: 4,
    category: 'evening',
    title: 'Duaja e Mbrëmjes',
    albanian: 'Arritëm në mbrëmje dhe sundimi i takon Allahut, dhe falënderimi i takon Allahut.',
    source: 'Muslimi',
    benefits: 'Mbrojtje gjatë natës'
  },
  {
    id: 5,
    category: 'evening',
    title: 'Para Gjumit',
    albanian: 'Me emrin Tënd, o Allah, vdes dhe jetoj.',
    source: 'Buhariu',
    benefits: 'Mbrojtje gjatë gjumit'
  },
  {
    id: 6,
    category: 'prayer',
    title: 'Pas Ezanit',
    albanian: 'O Allah, Zot i kësaj thirrjeje të plotë dhe namazit të përhershëm, jepi Muhamedit ndërmjetësimin dhe gradat e larta.',
    source: 'Buhariu',
    benefits: 'Ndërmjetësimi i Profetit në Ditën e Gjykimit'
  },
  {
    id: 7,
    category: 'prayer',
    title: 'Hyrja në Xhami',
    albanian: 'O Allah, më hap dyert e mëshirës Tënde.',
    source: 'Muslimi',
    benefits: 'Bereqet kur hyni në xhami'
  },
  {
    id: 8,
    category: 'prayer',
    title: 'Dalja nga Xhamia',
    albanian: 'O Allah, unë kërkoj nga mirësia Jote.',
    source: 'Muslimi',
    benefits: 'Kërkim i bereqetit të Allahut'
  },
  {
    id: 9,
    category: 'food',
    title: 'Para Ngrënies',
    albanian: 'Në emër të Allahut.',
    source: 'Buhariu',
    benefits: 'Bereqet në ushqim'
  },
  {
    id: 10,
    category: 'food',
    title: 'Pas Ngrënies',
    albanian: 'Falënderimi i takon Allahut, i Cili më ushqeu me këtë dhe ma furnizoi pa fuqi dhe pa mundësi prej meje.',
    source: 'Tirmidhiu',
    benefits: 'Faljen e mëkateve'
  },
  {
    id: 11,
    category: 'travel',
    title: 'Fillimi i Udhëtimit',
    albanian: 'I pastër nga çdo e metë është Ai që na nënshtroi këtë, sepse ne nuk kishim mundësi ta nënshtronim, dhe ne tek Zoti ynë do të kthehemi.',
    source: 'Muslimi',
    benefits: 'Mbrojtje gjatë udhëtimit'
  },
  {
    id: 12,
    category: 'travel',
    title: 'Kthimi nga Udhëtimi',
    albanian: 'Jemi kthyer, penduar, adhurues të Zotit tonë, falënderues.',
    source: 'Muslimi',
    benefits: 'Falënderim për kthimin e sigurt'
  },
  {
    id: 13,
    category: 'protection',
    title: 'Mbrojtje nga Çdo e Keqe',
    albanian: 'Kërkoj mbrojtje me fjalët e plota të Allahut nga e keqja e çdo gjëje që Ai ka krijuar.',
    source: 'Muslimi',
    benefits: 'Mbrojtje nga çdo e keqe'
  },
  {
    id: 14,
    category: 'protection',
    title: 'Ajetul Kursi',
    albanian: 'Allahu! Nuk ka zot tjetër përveç Tij, të Gjallit, Mbikëqyrësit të Përhershëm.',
    source: 'Kuran 2:255',
    benefits: 'Mbrojtje e fuqishme - lexoje pas çdo namazi'
  },
  {
    id: 15,
    category: 'forgiveness',
    title: 'Sejjidul Istigfar',
    albanian: 'O Allah, Ti je Zoti im, nuk ka zot tjetër përveç Teje. Ti më krijove dhe unë jam robi Yt.',
    source: 'Buhariu',
    benefits: 'Zotëria e istigfarit - falje e mëkateve'
  },
  {
    id: 16,
    category: 'forgiveness',
    title: 'Istigfar i Thjeshtë',
    albanian: 'Kërkoj falje nga Allahu i Madhërishëm, përveç të Cilit nuk ka zot tjetër, i Gjalli, Mbikëqyrësi i Përhershëm, dhe pendohem tek Ai.',
    source: 'Ebu Davudi',
    benefits: 'Falje e mëkateve'
  },
  {
    id: 17,
    category: 'daily',
    title: 'Hyrja në Shtëpi',
    albanian: 'Me emrin e Allahut hymë, me emrin e Allahut dolëm, dhe tek Allahu, Zoti ynë, u mbështetëm.',
    source: 'Ebu Davudi',
    benefits: 'Bereqet në shtëpi'
  },
  {
    id: 18,
    category: 'daily',
    title: 'Kur Shikon në Pasqyrë',
    albanian: 'O Allah, Ti e bukurosë krijimin tim, pra bukurosë edhe karakterin tim.',
    source: 'Ibn Hibban',
    benefits: 'Përsosmëri e karakterit'
  }
];

const DuaCollection = ({ isOpen, onClose, isAdmin, token, preview = false }) => {
  const isModal = typeof isOpen === 'boolean';
  const [duas, setDuas] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDua, setSelectedDua] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'morning',
    albanian: '',
    source: '',
    benefits: ''
  });
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('favoriteDuas');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    if (!isModal || isOpen) fetchDuas();
  }, [isModal, isOpen]);

  const fetchDuas = async () => {
    try {
      const res = await axios.get(`${API}/duas`);
      setDuas([...(res.data || []), ...defaultDuas]);
    } catch (error) {
      setDuas(defaultDuas);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.albanian) {
      toast.error('Titulli dhe teksti janë të detyrueshme');
      return;
    }

    try {
      await axios.post(`${API}/admin/duas`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Duaja u shtua me sukses');
      setUploadOpen(false);
      setFormData({ title: '', category: 'morning', albanian: '', source: '', benefits: '' });
      fetchDuas();
    } catch (error) {
      toast.error('Gabim gjatë ruajtjes');
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (typeof id === 'number') {
      toast.error('Duatë bazë nuk mund të fshihen');
      return;
    }
    if (!window.confirm('Dëshironi ta fshini këtë dua?')) return;
    try {
      await axios.delete(`${API}/admin/duas/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('U fshi me sukses');
      fetchDuas();
    } catch (error) {
      toast.error('Gabim gjatë fshirjes');
    }
  };

  const categoryTabs = useMemo(() => {
    const categoryIds = [...new Set(duas.map((dua) => dua.category).filter(Boolean))];
    const tabs = duaCategories.filter((category) => category.id === 'all' || categoryIds.includes(category.id));
    const knownIds = tabs.map((category) => category.id);
    const extraTabs = categoryIds
      .filter((id) => !knownIds.includes(id))
      .map((id) => ({ id, name: id, icon: '📌' }));
    return [...tabs, ...extraTabs];
  }, [duas]);

  const filteredDuas = duas.filter((dua) => {
    const matchesCategory = selectedCategory === 'all' || dua.category === selectedCategory;
    const matchesSearch = searchQuery === '' ||
      dua.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (dua.albanian || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const visibleDuas = preview ? filteredDuas.slice(0, 6) : filteredDuas;

  const handleCopy = (dua) => {
    const text = `${dua.title}\n\n${dua.albanian}\n\n(${dua.source || 'Pa burim'})`;
    navigator.clipboard.writeText(text);
    setCopiedId(dua.id);
    toast.success('Duaja u kopjua!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleFavorite = (duaId) => {
    const newFavorites = favorites.includes(duaId)
      ? favorites.filter((id) => id !== duaId)
      : [...favorites, duaId];
    setFavorites(newFavorites);
    localStorage.setItem('favoriteDuas', JSON.stringify(newFavorites));
  };

  const renderUploadButton = () => {
    if (!isAdmin) return null;

    return (
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogTrigger asChild>
          <button className="w-11 h-11 bg-primary text-white rounded-xl flex items-center justify-center hover:bg-primary/90 transition-all">
            <Plus className="w-5 h-5" />
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Shto Dua të Re</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 pt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titulli</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Titulli..." />
              </div>
              <div className="space-y-2">
                <Label>Kategoria</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {duaCategories.filter((category) => category.id !== 'all').map((category) => (
                      <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Teksti</Label>
              <Textarea
                value={formData.albanian}
                onChange={(e) => setFormData({ ...formData, albanian: e.target.value })}
                placeholder="Shkruani duanë..."
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Burimi (Opsionale)</Label>
                <Input value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} placeholder="Buhariu..." />
              </div>
              <div className="space-y-2">
                <Label>Dobitë (Opsionale)</Label>
                <Input value={formData.benefits} onChange={(e) => setFormData({ ...formData, benefits: e.target.value })} placeholder="Mbrojtje..." />
              </div>
            </div>

            <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
              Ruaj Duanë
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  const renderTabs = () => (
    <div className="flex gap-2 overflow-x-auto pb-2">
      {categoryTabs.map((category) => (
        <button
          key={category.id}
          onClick={() => {
            setSelectedCategory(category.id);
            setSelectedDua(null);
          }}
          className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedCategory === category.id
              ? 'bg-primary text-white'
              : 'bg-primary/10 text-primary hover:bg-primary/15'
          }`}
        >
          <span>{category.icon}</span>
          {category.name}
        </button>
      ))}
    </div>
  );

  const renderCards = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {visibleDuas.map((dua) => (
        <button
          key={dua.id}
          onClick={() => setSelectedDua(dua)}
          className="text-left p-5 bg-white border rounded-xl hover:shadow-md transition-all group"
        >
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-lg text-gray-800 group-hover:text-primary line-clamp-2">
                {dua.title}
              </h3>
              <p className="text-sm text-gray-500 mt-3 line-clamp-3">{dua.albanian}</p>
              {dua.source && (
                <p className="text-xs text-emerald-700 mt-3">{dua.source}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && typeof dua.id === 'string' && (
                <span onClick={(e) => handleDelete(e, dua.id)} className="p-2 text-red-300 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </span>
              )}
              <ChevronRight className="w-5 h-5 text-gray-300" />
            </div>
          </div>
        </button>
      ))}
    </div>
  );

  const renderDetail = () => (
    <div className="max-w-3xl mx-auto bg-white border rounded-2xl p-6">
      <button onClick={() => setSelectedDua(null)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6">
        <ChevronRight className="w-5 h-5 rotate-180" /> Kthehu
      </button>
      <h3 className="text-2xl font-bold text-gray-800 mb-2">{selectedDua.title}</h3>
      {selectedDua.source && <p className="text-sm text-gray-500 mb-6">{selectedDua.source}</p>}
      <div className="bg-amber-50 rounded-xl p-5 mb-4">
        <p className="text-gray-800 text-lg leading-relaxed">{selectedDua.albanian}</p>
      </div>
      {selectedDua.benefits && (
        <div className="bg-green-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-green-600 mb-1">Dobitë:</p>
          <p className="text-gray-700">{selectedDua.benefits}</p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={() => handleCopy(selectedDua)} className="flex-1 flex items-center justify-center gap-2 py-3 bg-gray-100 rounded-xl">
          {copiedId === selectedDua.id ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />} Kopjo
        </button>
        <button onClick={() => toggleFavorite(selectedDua.id)} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl ${favorites.includes(selectedDua.id) ? 'bg-red-100 text-red-500' : 'bg-gray-100'}`}>
          <Heart className={`w-5 h-5 ${favorites.includes(selectedDua.id) ? 'fill-current' : ''}`} /> Preferuar
        </button>
      </div>
    </div>
  );

  const content = (
    <div className={preview ? 'container mx-auto px-4 md:px-6 lg:px-8' : ''}>
      {!preview && !isModal && (
        <div className="mb-8">
          <Link to="/" className="inline-flex text-primary hover:text-primary-700 font-medium mb-6">
            ← Kthehu
          </Link>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-primary">Duatë</h1>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-accent" />
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary">
              {preview ? 'Duatë' : 'Koleksioni i Duave'}
            </h2>
            <p className="text-muted-foreground">Lutje për çdo rast të jetës</p>
          </div>
        </div>
        {renderUploadButton()}
      </div>

      {!preview && (
        <div className="relative mb-5">
          <Search className="absolute left-4 top-3.5 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Kërko dua..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border rounded-xl outline-none focus:border-primary transition-colors"
          />
        </div>
      )}

      <div className="mb-6">{renderTabs()}</div>

      {selectedDua && !preview ? renderDetail() : renderCards()}

      {preview && (
        <div className="flex justify-center mt-8">
          <Link
            to="/duate"
            className="border border-emerald-800 text-emerald-800 px-6 py-2.5 rounded-lg font-medium hover:bg-emerald-800 hover:text-white transition-colors duration-200"
          >
            Shiko të gjitha duatë →
          </Link>
        </div>
      )}
    </div>
  );

  if (isModal && !isOpen) return null;

  if (isModal) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            className="bg-gray-50 rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end mb-2">
              <button onClick={onClose} className="w-11 h-11 bg-white rounded-xl flex items-center justify-center hover:bg-gray-100 transition-all">
                <X className="w-6 h-6" />
              </button>
            </div>
            {content}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <section id={preview ? 'duate' : undefined} className={preview ? 'py-12 md:py-16 bg-muted' : 'min-h-screen bg-background py-24'}>
      {content}
    </section>
  );
};

export default DuaCollection;
